/**
 * Contract List Page
 * Displays all contracts with filtering and sorting
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Description as ContractIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  RestartAlt as RestartAltIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { apiGet, apiDelete } from '../utils/apiClient';
import { usePermissions } from '../contexts/PermissionContext';
import { MODULES, PERMISSION_LEVELS } from '../contexts/PermissionConstants';
import { formatNumber } from '../utils/validation';
import ContractReferenceCell from '../components/contracts/ContractReferenceCell';
import usePagination from '../hooks/usePagination';
import useTableSort from '../hooks/useTableSort';
import LoadingSpinner from '../components/common/LoadingSpinner';
import SortableHeaderCell from '../components/common/SortableHeaderCell';
import { getStatusColor } from '../utils/statusColors';
import logger from '../utils/logger';

const SORTABLE_COLUMNS = {
  reference:                { label: 'Reference',                align: 'left'   },
  investor_name:            { label: 'Investor',                 align: 'left'   },
  product_type:             { label: 'Product',                  align: 'center' },
  rate_type:                { label: 'Rate Type',                align: 'center' },
  last_effective_rate_date: { label: 'Last Effective Rate Date', align: 'center' },
  interest_rate:            { label: 'Interest Rate',            align: 'center' },
  deposit_date:             { label: 'Start Date',               align: 'center' },
  maturity_date:            { label: 'Maturity Date',            align: 'center' },
  capital_amount:           { label: 'Capital Amount',           align: 'right'  },
  status:                   { label: 'Status',                   align: 'center' },
};

function ContractList() {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  const pagination = usePagination({ defaultRowsPerPage: 10 });
  const { page, rowsPerPage, resetPage } = pagination;

  // Deeplink support: /contracts?sort=maturity_date&dir=asc&status=Active seeds
  // sort + filter state. Allowlisted values only so a typo can't break the
  // table. Read once at mount.
  const [searchParams] = useSearchParams();
  const initialSort = useMemo(() => {
    const col = searchParams.get('sort');
    const dir = searchParams.get('dir');
    if (col && SORTABLE_COLUMNS[col] && (dir === 'asc' || dir === 'desc')) {
      return [{ column: col, dir }];
    }
    return [];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const initialStatus = useMemo(() => {
    const s = searchParams.get('status');
    // Allowlist matches the status dropdown options rendered below.
    const ALLOWED = [
      'Draft', 'Pending Approval', 'Approved', 'Active', 'Matured',
      'Matured - Rolled', 'Matured - Terminated', 'Early Terminated',
      'Finalised', 'Cancelled', 'Historical',
    ];
    return s && ALLOWED.includes(s) ? s : 'all';
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Deeplink from the dashboard "Contracts Requiring Action" card: ?needs_action=true.
  const initialNeedsAction = useMemo(
    () => searchParams.get('needs_action') === 'true',
  // eslint-disable-next-line react-hooks/exhaustive-deps
  []);
  const sort = useTableSort({ onSortChange: resetPage, defaultSort: initialSort });

  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [amendmentFilter, setAmendmentFilter] = useState('all');
  const [rateTypeFilter, setRateTypeFilter] = useState('all');
  const [needsActionFilter, setNeedsActionFilter] = useState(initialNeedsAction);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [contracts, setContracts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const canWrite = hasPermission(MODULES.CONTRACT_MANAGEMENT, PERMISSION_LEVELS.WRITE);

  // Debounce search input so every keystroke doesn't hit the API
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset to first page when any filter changes (sort handled by useTableSort onSortChange)
  useEffect(() => {
    resetPage();
  }, [statusFilter, amendmentFilter, rateTypeFilter, needsActionFilter, debouncedSearch, resetPage]);

  const fetchContracts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1, // API is 1-indexed, MUI is 0-indexed
        limit: rowsPerPage,
      };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (amendmentFilter !== 'all') params.amendment = amendmentFilter;
      if (rateTypeFilter !== 'all') params.rate_type = rateTypeFilter;
      if (needsActionFilter) params.needs_action = 'true';
      if (debouncedSearch) params.search = debouncedSearch;
      Object.assign(params, sort.apiParams);

      const response = await apiGet('/contracts', { params });
      setContracts(response?.data || []);
      setTotalCount(response?.pagination?.total || 0);
    } catch (err) {
      logger.error('Failed to load contracts', { message: err.message, status: err.response?.status });
      setContracts([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter, amendmentFilter, rateTypeFilter, needsActionFilter, debouncedSearch, sort.apiParams]);

  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  const handleViewContract = (id, status) => {
    if (status === 'Draft' || status === 'Pending Approval') {
      navigate(`/contracts/${id}/edit`);
    } else {
      navigate(`/contracts/${id}`);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Contracts</Typography>
        {canWrite && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/contracts/new')}
          >
            New Contract
          </Button>
        )}
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ minWidth: 250 }}
              placeholder="Reference or Investor..."
            />
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="Draft">Draft</MenuItem>
                <MenuItem value="Pending Approval">Pending Approval</MenuItem>
                <MenuItem value="Approved">Approved</MenuItem>
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Matured">Matured</MenuItem>
                <MenuItem value="Matured - Rolled">Matured - Rolled</MenuItem>
                <MenuItem value="Matured - Terminated">Matured - Terminated</MenuItem>
                <MenuItem value="Early Terminated">Early Terminated</MenuItem>
                <MenuItem value="Finalised">Finalised</MenuItem>
                <MenuItem value="Cancelled">Cancelled</MenuItem>
                <MenuItem value="Historical">Historical</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Amendment</InputLabel>
              <Select
                value={amendmentFilter}
                onChange={(e) => setAmendmentFilter(e.target.value)}
                label="Amendment"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="pending">Pending Amendment</MenuItem>
                <MenuItem value="none">No Pending Changes</MenuItem>
              </Select>
            </FormControl>
            <Divider orientation="vertical" flexItem />
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {['Draft', 'Pending Approval'].map((s) => (
                <Chip
                  key={s}
                  label={s}
                  color={statusFilter === s ? getStatusColor(s) : 'default'}
                  variant={statusFilter === s ? 'filled' : 'outlined'}
                  onClick={() => {
                    if (statusFilter === s) {
                      setStatusFilter('all');
                    } else {
                      setStatusFilter(s);
                      setAmendmentFilter('all');
                    }
                  }}
                  clickable
                />
              ))}
              <Chip
                label="Pending Amendment"
                color={amendmentFilter === 'pending' ? 'warning' : 'default'}
                variant={amendmentFilter === 'pending' ? 'filled' : 'outlined'}
                onClick={() => {
                  if (amendmentFilter === 'pending') {
                    setAmendmentFilter('all');
                  } else {
                    setAmendmentFilter('pending');
                    setStatusFilter('all');
                  }
                }}
                clickable
              />
              {['Active', 'Matured'].map((s) => (
                <Chip
                  key={s}
                  label={s}
                  color={statusFilter === s ? getStatusColor(s) : 'default'}
                  variant={statusFilter === s ? 'filled' : 'outlined'}
                  onClick={() => {
                    if (statusFilter === s) {
                      setStatusFilter('all');
                    } else {
                      setStatusFilter(s);
                      setAmendmentFilter('all');
                    }
                  }}
                  clickable
                />
              ))}
              {/* Requires Action: contracts past maturity not yet finalised/rolled and
                  not mid-amendment (server needs_action flag). Spans Active + Matured,
                  so clear the status filter when engaging it. */}
              <Chip
                icon={<WarningIcon />}
                label="Requires Action"
                color={needsActionFilter ? 'error' : 'default'}
                variant={needsActionFilter ? 'filled' : 'outlined'}
                onClick={() => {
                  if (needsActionFilter) {
                    setNeedsActionFilter(false);
                  } else {
                    setNeedsActionFilter(true);
                    setStatusFilter('all');
                    setAmendmentFilter('all');
                  }
                }}
                clickable
              />
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {['Fixed', 'Variable'].map((rt) => (
                <Chip
                  key={rt}
                  label={rt}
                  color={rateTypeFilter === rt ? 'primary' : 'default'}
                  variant={rateTypeFilter === rt ? 'filled' : 'outlined'}
                  onClick={() => setRateTypeFilter(rateTypeFilter === rt ? 'all' : rt)}
                  clickable
                />
              ))}
            </Box>
            {(statusFilter !== 'all' || amendmentFilter !== 'all' || rateTypeFilter !== 'all'
              || needsActionFilter || searchTerm || sort.sorts.length > 0) && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<RestartAltIcon />}
                onClick={() => {
                  setStatusFilter('all');
                  setAmendmentFilter('all');
                  setRateTypeFilter('all');
                  setNeedsActionFilter(false);
                  setSearchTerm('');
                  sort.reset();
                }}
                sx={{ ml: 'auto' }}
              >
                Reset
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {Object.entries(SORTABLE_COLUMNS).map(([key, col]) => (
                <SortableHeaderCell key={key} sort={sort} columnKey={key} align={col.align}>
                  {col.label}
                </SortableHeaderCell>
              ))}
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 6 }}>
                  <LoadingSpinner />
                </TableCell>
              </TableRow>
            ) : (
              <>
                {contracts.map((contract) => (
                  <TableRow
                    key={contract.id}
                    hover
                    onClick={() => handleViewContract(contract.id, contract.status)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <ContractReferenceCell reference={contract.reference} userReference={contract.user_reference} />
                    </TableCell>
                    <TableCell>{contract.investor_name}</TableCell>
                    <TableCell align="center">
                      <Chip label={contract.product_type} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell align="center">
                      {contract.rate_type
                        ? <Chip label={contract.rate_type} size="small" variant="outlined" />
                        : '-'}
                    </TableCell>
                    <TableCell align="center">
                      {contract.last_effective_rate_date
                        ? format(new Date(contract.last_effective_rate_date), 'dd MMM yyyy')
                        : '-'}
                    </TableCell>
                    <TableCell align="center">{contract.interest_rate}%</TableCell>
                    <TableCell align="center">
                      {contract.deposit_date ? format(new Date(contract.deposit_date), 'dd MMM yyyy') : '-'}
                    </TableCell>
                    <TableCell align="center">
                      {contract.maturity_date ? format(new Date(contract.maturity_date), 'dd MMM yyyy') : '-'}
                    </TableCell>
                    <TableCell align="right">
                      {contract.currency_code} {formatNumber(contract.capital_amount)}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <Chip
                          label={contract.status}
                          color={getStatusColor(contract.status)}
                          size="small"
                        />
                        {contract.needs_action && (
                          <Chip
                            icon={<WarningIcon />}
                            label="Action Needed"
                            size="small"
                            color="error"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                        {contract.rolled_from_contract_id && (
                          <Chip
                            label="Rollover"
                            size="small"
                            color="info"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                        {contract.amendment_status && (
                          <Chip
                            icon={<WarningIcon />}
                            label={contract.amendment_status}
                            size="small"
                            color="warning"
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <IconButton size="small" onClick={() => handleViewContract(contract.id, contract.status)} title="Open Contract">
                        <ContractIcon fontSize="small" />
                      </IconButton>
                      {contract.status === 'Draft' && (
                        <IconButton
                          size="small"
                          color="error"
                          title="Delete Draft"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm(`Delete draft contract ${contract.reference}? This cannot be undone.`)) {
                              apiDelete(`/contracts/${contract.id}`).then(() => {
                                fetchContracts();
                              }).catch(err => {
                                alert('Failed to delete: ' + (err.response?.data?.message || err.message));
                              });
                            }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {contracts.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={11} align="center">
                      <Typography color="textSecondary" sx={{ py: 3 }}>
                        No contracts found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </>
            )}
          </TableBody>
        </Table>
        <TablePagination {...pagination.tableProps(totalCount)} />
      </TableContainer>
    </Box>
  );
}

export default ContractList;
